import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { apiClient } from '@/api/apiClient';
import { useCategories } from '@/hooks/useCategories';
import { mobileStorage } from '@/utils/mobileStorage';

const ONBOARDING_STORAGE_KEY = 'pepite_onboarding_tasks';
const STREAK_STORAGE_KEY = 'pepite_streak_data';

// ─── Singleton store so all components share the same state ────
let _completedTasks = [];
let _streakCount = 0;
let _isLoaded = false;
let _listeners = new Set();
let _initialLoadDone = false;

function _notify() {
    _listeners.forEach(fn => fn());
}

function _subscribe(listener) {
    _listeners.add(listener);
    return () => _listeners.delete(listener);
}

function _persistStreakData(data) {
    const serialized = JSON.stringify(data);
    localStorage.setItem(STREAK_STORAGE_KEY, serialized);
    if (Capacitor.isNativePlatform()) {
        mobileStorage.setItem(STREAK_STORAGE_KEY, serialized);
        mobileStorage.setItem('streak_data', serialized);
    }
}

function _persistOnboardingTasks(tasks) {
    const serialized = JSON.stringify(tasks);
    localStorage.setItem(ONBOARDING_STORAGE_KEY, serialized);
    if (Capacitor.isNativePlatform()) {
        mobileStorage.setItem(ONBOARDING_STORAGE_KEY, serialized);
    }
}

// Load from localStorage (and hydrate from Capacitor Preferences on mobile)
function _loadLocalData() {
    try {
        const tasks = JSON.parse(localStorage.getItem(ONBOARDING_STORAGE_KEY) || '[]');
        _completedTasks = Array.isArray(tasks) ? tasks : [];

        const streakDataStr = localStorage.getItem(STREAK_STORAGE_KEY) || localStorage.getItem('streak_data');
        if (streakDataStr) {
            const data = JSON.parse(streakDataStr);
            const today = new Date().toISOString().split('T')[0];
            const lastVisit = data.lastVisit;

            if (lastVisit !== today) {
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                const yesterdayStr = yesterday.toISOString().split('T')[0];

                _streakCount = (lastVisit === yesterdayStr) ? (data.count || 0) + 1 : 1;
                _persistStreakData({ count: _streakCount, lastVisit: today });
            } else {
                _streakCount = data.count || 0;
            }
        } else {
            _streakCount = 1;
            _persistStreakData({ count: 1, lastVisit: new Date().toISOString().split('T')[0] });
        }
    } catch (e) {
        console.error('Failed to parse local onboarding data', e);
    }
}

// Load immediately so data is ready before first render
_loadLocalData();
_isLoaded = true;

// On native mobile, also ensure Preferences is hydrated and re-evaluate if needed
if (Capacitor.isNativePlatform()) {
    Promise.all([
        mobileStorage.getItem(STREAK_STORAGE_KEY),
        mobileStorage.getItem('streak_data'),
        mobileStorage.getItem(ONBOARDING_STORAGE_KEY),
    ]).then(([nativeStreak, nativeStreakAlias, nativeTasks]) => {
        let updated = false;
        const rawStreak = nativeStreak || nativeStreakAlias;
        if (rawStreak) {
            try {
                const parsed = JSON.parse(rawStreak);
                if (parsed && typeof parsed.count === 'number' && parsed.count > _streakCount) {
                    _streakCount = parsed.count;
                    localStorage.setItem(STREAK_STORAGE_KEY, rawStreak);
                    updated = true;
                }
            } catch {
                // ignore
            }
        }
        if (nativeTasks) {
            try {
                const parsedTasks = JSON.parse(nativeTasks);
                if (Array.isArray(parsedTasks) && parsedTasks.length > 0) {
                    _completedTasks = [...new Set([..._completedTasks, ...parsedTasks])];
                    localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(_completedTasks));
                    updated = true;
                }
            } catch {
                // ignore
            }
        }
        if (updated) {
            _notify();
        }
    }).catch(() => {});
}

export function useOnboarding() {
    const { isAuthenticated, user } = useCategories();

    // Force re-render when singleton store changes
    const [, forceUpdate] = useState(0);

    useEffect(() => {
        const unsub = _subscribe(() => forceUpdate(v => v + 1));
        return unsub;
    }, []);

    // If authenticated, sync from user data (once)
    useEffect(() => {
        if (isAuthenticated && user && !_initialLoadDone) {
            _initialLoadDone = true;

            if (user.onboarding_completed_tasks && user.onboarding_completed_tasks.length > 0) {
                // Merge: take union of local + remote
                const localTasks = _completedTasks;
                const remoteTasks = user.onboarding_completed_tasks;
                const merged = [...new Set([...localTasks, ...remoteTasks])];
                _completedTasks = merged;
            }

            if (user.streak_count) {
                _streakCount = user.streak_count;
                _persistStreakData({
                    count: _streakCount,
                    lastVisit: user.last_visit_date || new Date().toISOString().split('T')[0]
                });
            }

            // Check if backend streak needs update
            const today = new Date().toISOString().split('T')[0];
            if (user.last_visit_date !== today) {
                apiClient.auth.updateOnboarding({}).then(data => {
                    if (data) {
                        _streakCount = data.streak_count || 1;
                        _persistStreakData({
                            count: _streakCount,
                            lastVisit: data.last_visit_date || today
                        });
                        if (data.onboarding_completed_tasks) {
                            const merged = [...new Set([..._completedTasks, ...data.onboarding_completed_tasks])];
                            _completedTasks = merged;
                            _persistOnboardingTasks(_completedTasks);
                        }
                        _notify();
                    }
                }).catch(() => {});
            }

            // Sync local tasks
            _persistOnboardingTasks(_completedTasks);
            _notify();
        }
    }, [isAuthenticated, user]);

    const completeTask = useCallback((taskId) => {
        if (_completedTasks.includes(taskId)) return;

        _completedTasks = [..._completedTasks, taskId];

        // Always save to localStorage and native Preferences
        _persistOnboardingTasks(_completedTasks);

        // Notify all subscribers immediately
        _notify();

        // If authenticated, also sync to backend (fire and forget)
        if (isAuthenticated) {
            apiClient.auth.updateOnboarding({ completed_task: taskId }).catch(e => {
                console.error('Failed to sync completed task', e);
            });
        }
    }, [isAuthenticated]);

    return {
        completedTasks: _completedTasks,
        streakCount: _streakCount,
        completeTask,
        isLoaded: _isLoaded
    };
}
